import {
  ArrowsAltOutlined,
  CloseOutlined,
  MinusOutlined,
  SwitcherOutlined,
} from '@ant-design/icons'
import type { ModalProps } from 'antd'
import { Button, Modal, Space } from 'antd'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import type { DraggableEventHandler } from 'react-draggable'
import Draggable from 'react-draggable'
import { useIntl } from 'umi'

import styles from './index.less'

export type DraggableModalProps = ModalProps & {
  children?: React.ReactNode
  defaultMode?: SizeMode
  allowSetModal?: boolean
}

type SizeMode = 'MIN' | 'NORMAL' | 'MAX'

type Bounds = {
  left: number
  top: number
  bottom: number
  right: number
}

export type DraggableModalRefProps = {
  setMode: (mode: SizeMode) => void
}

const DraggableModal = forwardRef<DraggableModalRefProps, DraggableModalProps>(
  (props, ref) => {
    const {
      defaultMode = 'NORMAL',
      allowSetModal = true,
      ...modalProps
    } = props

    // 国际化
    const intl = useIntl()
    const c = window.$tc('', intl.formatMessage)
    const t = window.$tc('component.DraggableModal.index', intl.formatMessage)

    const draggleRef = useRef<HTMLDivElement>(null)
    const [disabledDrag, setDisabledDrag] = useState<boolean>(true)
    const [bounds, setBounds] = useState<Bounds>({
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
    })

    const [mode, setMode] = useState<SizeMode>(defaultMode)

    // 拖拽事件
    const onDragStart: DraggableEventHandler = (_e, data) => {
      const rect = draggleRef?.current?.getBoundingClientRect() as DOMRect
      if (window.__POWERED_BY_QIANKUN__) {
        const shadowRoot = draggleRef.current?.getRootNode() as ShadowRoot
        const container = shadowRoot.querySelector('#root') as HTMLDivElement
        const top = -(draggleRef.current?.parentElement?.offsetTop ?? 0)
        const left = -(container.clientWidth - rect.width) / 2
        setBounds({
          left,
          right: -left,
          top,
          bottom: container.clientHeight - rect.height + top,
        })
        return
      }
      const { clientWidth, clientHeight } = document.documentElement
      setBounds({
        left: -rect?.left + data.x,
        right: clientWidth - (rect?.right - data.x),
        top: -rect?.top + data.y,
        bottom: clientHeight - (rect?.bottom - data.y),
      })
    }

    useImperativeHandle(ref, () => ({
      setMode,
    }))

    const triggerMode = (key: SizeMode) => {
      console.log(key, mode)
      if (key === 'MIN') {
        setMode('MIN')
      } else if (mode === 'NORMAL') {
        setMode('MAX')
      } else {
        setMode('NORMAL')
      }
    }

    const oprationRender = (
      <Space>
        {allowSetModal && (
          <Button
            size="small"
            icon={<MinusOutlined />}
            onClick={() => triggerMode('MIN')}
          />
        )}
        {allowSetModal && (
          <Button
            size="small"
            icon={
              mode === 'NORMAL' ? <ArrowsAltOutlined /> : <SwitcherOutlined />
            }
            onClick={() => triggerMode('NORMAL')}
          />
        )}
        <Button
          size="small"
          icon={<CloseOutlined />}
          onClick={props.onCancel}
        />
      </Space>
    )

    const titleRender = (
      <div
        className={styles['ant-design-draggable-header']}
        onMouseOver={() => {
          if (disabledDrag && mode !== 'MAX') {
            setDisabledDrag(false)
          }
        }}
        onMouseOut={() => setDisabledDrag(true)}
      >
        <div className={styles['ant-design-draggable-header-title']}>
          {(props.title as string) || t('t0', '提示')}
        </div>
        {oprationRender}
      </div>
    )

    useEffect(() => {
      const dragDiv = draggleRef.current
      if (!!dragDiv) {
        if (mode === 'MAX') {
          dragDiv.style.top = '0 !important'
          dragDiv.classList.add(`${styles['no-transform']}`)
        } else {
          dragDiv.classList.remove(`${styles['no-transform']}`)
        }
      }
    }, [mode])

    const modalRender: (node: React.ReactNode) => React.ReactNode = (modal) => (
      // @ts-ignore
      <Draggable disabled={disabledDrag} bounds={bounds} onStart={onDragStart}>
        <div ref={draggleRef}>{modal as JSX.Element}</div>
      </Draggable>
    )

    return props.visible ? (
      <>
        {mode !== 'MAX' && (
          <style>{`body { width: 100% !important; overflow: auto !important }`}</style>
        )}
        <Modal
          mask={false}
          closable={false}
          {...modalProps}
          title={titleRender}
          modalRender={modalRender}
          wrapClassName={styles['ant-design-draggable-modal-wrap']}
          className={`
          ${styles['ant-design-draggable-modal']}
          ${mode === 'MIN' && styles['ant-design-draggable-modal-min']}
          ${
            mode === 'MAX' &&
            (window.__POWERED_BY_QIANKUN__
              ? styles[
                  `ant-design-draggable-modal-max-qiankun${props.footer === false ? '-no-footer' : ''}`
                ]
              : styles[
                  `ant-design-draggable-modal-max${props.footer === false ? '-no-footer' : ''}`
                ])
          }
        `}
        >
          {props.children}
        </Modal>
      </>
    ) : null
  },
)

export default DraggableModal
